import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { guestPaymentService } from './guestPaymentService';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import tykvenLogo from '../../shared/assets/logopng.png';
import { useAuth } from '../../contexts/AuthContext';
import './GuestPaymentPage.css';

const loadRazorpay = () =>
    new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

const parseLogoUrl = (raw) => {
    try {
        const parsed = JSON.parse(raw);
        const path = Array.isArray(parsed) ? parsed[0] : parsed;
        return `http://tykven.local/storage/${path}`;
    } catch { return null; }
};

const fmt = (val, currency) =>
    `${currency || ''} ${parseFloat(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const LockIcon = () => (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

const CardIcon = () => (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
);

const ShieldIcon = () => (
    <svg fill="currentColor" viewBox="0 0 20 20" style={{ color: '#16a34a' }}>
        <path fillRule="evenodd" clipRule="evenodd"
            d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
    </svg>
);

const PciIcon = () => (
    <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 7H7v6h6V7z" />
        <path fillRule="evenodd" clipRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9 9V5a1 1 0 112 0v4h4a1 1 0 110 2h-4v4a1 1 0 11-2 0v-4H5a1 1 0 110-2h4z" />
    </svg>
);

const BellIcon = () => (
    <svg fill="currentColor" viewBox="0 0 20 20" style={{ color: '#1e40af' }}>
        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zm0 16a2 2 0 01-2-2h4a2 2 0 01-2 2z" />
    </svg>
);

const GuestPaymentPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuth } = useAuth();

    const [pageData, setPageData] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [failureReason, setFailureReason] = useState(null);
    const [error, setError] = useState(null);

    const paymentDoneRef = useRef(false);
    const pendingFailureRef = useRef(null);

    useEffect(() => {
        guestPaymentService(token).validateToken()
            .then((res) => setPageData(res.data?.data || res.data))
            .catch(() => setTokenError('Invalid or expired payment link.'));
    }, [token]);

    const extractErrorMessage = (err, fallback) => {
        const data = err?.response?.data;
        if (data?.errors?.length) return data.errors.map(e => e.message).join(' ');
        return data?.message || fallback;
    };

    const handlePay = async () => {
        setLoading(true);
        setError(null);
        paymentDoneRef.current = false;
        pendingFailureRef.current = null;
        const svc = guestPaymentService(token);
        try {
            const loaded = await loadRazorpay();
            if (!loaded) throw new Error('Failed to load Razorpay SDK.');

            const initRes = await svc.initialise({  checksum: pageData?.checksum });
            const { payment_guid, order_id, public_key, amount, currency } = initRes.data?.data || initRes.data;

            const options = {
                key: public_key,
                amount: Math.round(parseFloat(amount)),
                currency: currency || 'INR',
                order_id,
                name: pageData?.organisation?.name || 'Tykven',
                description: `Invoice ${pageData?.invoice?.number}`,
                image: parseLogoUrl(pageData?.organisation?.logo_url),
                handler: async (response) => {
                    paymentDoneRef.current = true;
                    try {
                        await svc.verify({
                            payment_guid,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        setSuccessData({ amount: pageData?.invoice?.total, currency: pageData?.invoice?.currency });
                    } catch {
                        setFailureReason('Payment verification failed. Please contact support.');
                        setLoading(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        if (!paymentDoneRef.current) {
                            setFailureReason(pendingFailureRef.current || 'Payment was not completed.');
                        }
                        setLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (resp) => {
                pendingFailureRef.current = resp.error?.description || 'Payment failed.';
            });
            rzp.open();
        } catch (err) {
            setError(extractErrorMessage(err, err.message || 'Payment initialisation failed.'));
            setLoading(false);
        }
    };

    if (tokenError) {
        return (
            <div className="gp-center">
                <div className="gp-center-card">
                    <div style={{ fontSize: 36 }}>🔗</div>
                    <h3>Link Expired</h3>
                    <p>{tokenError}</p>
                    <button
                        className="gp-expired-btn"
                        onClick={() => navigate(isAuth ? '/dashboard' : '/login')}
                    >
                        {isAuth ? 'Go to Dashboard' : 'Go to Home'}
                    </button>
                </div>
            </div>
        );
    }

    if (!pageData) {
        return (
            <div className="gp-center">
                <div className="gp-center-card">
                    <div className="gp-page-spinner" />
                    <p style={{ marginTop: 16 }}>Loading payment details…</p>
                </div>
            </div>
        );
    }

    if (successData) {
        return <PaymentSuccess amount={successData.amount} currency={successData.currency} onClose={() => navigate(isAuth ? '/dashboard' : '/login')} />;
    }

    if (failureReason) {
        return <PaymentFailure reason={failureReason} onClose={() => setFailureReason(null)} />;
    }

    const { invoice, organisation, payment_method } = pageData;
    const currency = invoice?.currency || 'INR';
    const logoUrl = parseLogoUrl(organisation?.logo_url);

    return (
        <div className="gp-page">

            {/* Nav */}
            <nav className="gp-nav">
                <div className="gp-nav-brand">
                    <img src={tykvenLogo} alt="Tykven" className="gp-nav-logo" />
                    <span className="gp-nav-name">Tykven</span>
                </div>
                {/* <div className="gp-nav-invoice">
                    Invoice: <span>{invoice?.number}</span>
                </div> */}
            </nav>

            {/* Main */}
            <main className="gp-main">
                <div className="gp-grid">

                    {/* Left — Order Summary */}
                    <section className="gp-summary">
                        <div className="gp-summary-heading">
                            <h1>Review your payment</h1>
                            <p>Secure checkout for {organisation?.name}</p>
                        </div>

                        <div className="gp-summary-card">
                            <div className="gp-summary-invoice-ref">
                                <span>Invoice</span>
                                <span>{invoice?.number}</span>
                            </div>
                            <div className="gp-summary-subtotal">
                                <span>Subtotal</span>
                                <span>{fmt(invoice?.amount, currency)}</span>
                            </div>

                            <div className="gp-summary-details">
               
                                {invoice?.tax > 0 && (
                                    <div className="gp-summary-row">
                                        <span>Tax</span>
                                        <span>{fmt(invoice?.tax, currency)}</span>
                                    </div>
                                )}
                                <div className="gp-summary-total-block">
                                    <p className="gp-summary-total-label">Total Amount Due</p>
                                    <p className="gp-summary-total-value">{fmt(invoice?.total, currency)}</p>
                                </div>
                            </div>

                            <div className="gp-merchant-info">
                                <div>
                                    <p className="gp-merchant-name">{organisation?.name}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Right — Payment Methods */}
                    <section className="gp-payment-panel">
                        <h2>Choose Payment Method</h2>

                        <div className="gp-methods-list">
                            {/* Active method */}
                            <div className="gp-method-option active">
                                <div className="gp-method-option-left">
                                    <div className="gp-method-option-icon"><CardIcon /></div>
                                    <div>
                                        <p className="gp-method-option-name">
                                            {payment_method?.display_name || 'Pay Online (Card/UPI/NetBanking)'}
                                        </p>
                                        <p className="gp-method-option-desc">Supports all major Indian banks and cards</p>
                                    </div>
                                </div>
                                <div className="gp-radio-dot">
                                    <div className="gp-radio-dot-inner" />
                                </div>
                            </div>

                        </div>

                        <div className="gp-cta">
                            {error && <div className="gp-error">{error}</div>}

                            <button className="gp-pay-btn" onClick={handlePay} disabled={loading}>
                                {loading ? (
                                    <><div className="gp-spinner" /> Processing…</>
                                ) : (
                                    <><LockIcon /> Pay {fmt(invoice?.total, currency)}</>
                                )}
                            </button>

                            <p className="gp-terms">
                                By clicking the button, you agree to our{' '}
                                <a href="https://tykven.com/licenses-and-agreements/" target="_blank" rel="noreferrer">Terms of Service</a> and{' '}
                                <a href="https://tykven.com/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>.
                            </p>
                        </div>
                    </section>

                </div>
            </main>

            {/* Footer */}
            <footer className="gp-footer">
                <div className="gp-footer-inner">
                    <div className="gp-trust-badges">
                        <div className="gp-trust-badge">
                            <ShieldIcon />
                            <span>Secure Payment</span>
                        </div>
                        <div className="gp-trust-badge">
                            <PciIcon />
                            <span>Instant Confirmation</span>
                        </div>
                        <div className="gp-trust-badge">
                            <BellIcon />
                            <span>Real-time Notifications</span>
                        </div>
                    </div>
                    <div className="gp-powered">
                        <span className="gp-powered-label">Facility Management by</span>
                        <div className="gp-powered-by">
                            <img src={tykvenLogo} alt="Tykven" style={{ height: 18, width: 'auto' }} />
                            <span>Tykven</span>
                        </div>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default GuestPaymentPage;
